/**
 * PiP Window — Real browser-level Document Picture-in-Picture.
 *
 * Uses the Document Picture-in-Picture API to open the game canvas in a native
 * OS-level always-on-top window. The Phaser canvas is mirrored via
 * captureStream() into a <video> element inside the PiP window.
 *
 * Cross-window controls: buttons in the PiP window call back to the opener
 * (main page) via window.opener, which exposes a PD.pipAPI namespace.
 *
 * @module ui/pip-window
 */

import { bus } from '../core/events.js';
import { isMuted, setMuted } from './sfx.js';

const PIP_WIDTH = 440;
const PIP_HEIGHT = 340;

/**
 * Minimal inline stylesheet for the PiP window document.
 * We can't link external CSS because the PiP document is a blank slate.
 */
function buildStyles(origin) {
  return `
    :root {
      --pd-bg: #1a1620;
      --pd-gold: #e8b454;
      --pd-text: #e8e1d6;
      --pd-muted: #8a8194;
      --pd-surface: #2c2118;
      --pd-border: #4a3a26;
      --pd-chrome-header-start: #7a1f1f;
      --pd-chrome-header-end: #591616;
      --pd-chrome-header-border: #b8862f;
      --pd-chrome-text: #ffd98a;
      --pd-chrome-accent: #ffd0a8;
      --pd-transition: 0.18s ease;
      --pd-rarity-common: #9d9d9d;
      --pd-rarity-uncommon: #1eff00;
      --pd-rarity-rare: #0070dd;
      --pd-rarity-epic: #a335ee;
      --pd-rarity-legendary: #ff8000;
    }

    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      background: var(--pd-bg);
      background-clip: padding-box;
      color: var(--pd-text);
      font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
      font-size: 12px;
      overflow: hidden;
      user-select: none;
      -webkit-user-select: none;
      display: flex;
      flex-direction: column;
      height: 100vh;
      /* Same gothic carved-stone frame as the site game window (#pd-pip). */
      border: 11px solid transparent;
      border-image: url('${origin}/assets/sprites/ui/frame_gothic.png') 75 repeat;
      box-sizing: border-box;
      position: relative;
    }

    /* ---- Titlebar ---- */
    .pd-pipw-titlebar {
      position: relative;
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      background: linear-gradient(to bottom, var(--pd-chrome-header-start), var(--pd-chrome-header-end));
      border-bottom: 1px solid var(--pd-chrome-header-border);
      flex-shrink: 0;
    }

    /* Logo + name always sit dead-center in the titlebar. */
    .pd-pipw-brand {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      align-items: center;
      gap: 6px;
      pointer-events: none;
      max-width: 55%;
    }

    .pd-pipw-logo {
      height: 16px;
      width: auto;
      image-rendering: pixelated;
      flex-shrink: 0;
    }

    .pd-pipw-label {
      font-size: 10px;
      font-weight: 700;
      color: var(--pd-chrome-text);
      letter-spacing: 0.5px;
      text-shadow: 0 1px 0 rgba(0,0,0,0.5);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .pd-pipw-menu-toggle {
      width: 22px;
      height: 22px;
      background: none;
      border: 1px solid transparent;
      border-radius: 4px;
      color: var(--pd-chrome-accent);
      font-size: 13px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.18s ease, border-color 0.18s ease;
      padding: 0;
      flex-shrink: 0;
    }

    .pd-pipw-menu-toggle:hover {
      background: rgba(255,255,255,0.1);
      border-color: rgba(255,255,255,0.15);
    }

    .pd-pipw-menu-toggle.active {
      background: rgba(232, 180, 84, 0.18);
      border-color: var(--pd-gold);
      color: var(--pd-gold);
    }

    /* ---- Toolbar ---- */
    .pd-pipw-toolbar {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 3px 6px;
      background: var(--pd-surface);
      border-bottom: 1px solid var(--pd-border);
      flex-shrink: 0;
    }

    .pd-pipw-toolbar button {
      width: 28px;
      height: 24px;
      background: none;
      border: 1px solid transparent;
      border-radius: 4px;
      color: var(--pd-muted);
      font-size: 13px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all var(--pd-transition);
      padding: 0;
      line-height: 1;
    }

    .pd-pipw-toolbar button:hover {
      background: rgba(232, 180, 84, 0.12);
      border-color: rgba(232, 180, 84, 0.3);
      color: var(--pd-gold);
    }

    .pd-tool-icon {
      width: 20px;
      height: 20px;
      image-rendering: pixelated;
      display: block;
      pointer-events: none;
    }

    .pd-pipw-spacer {
      flex: 1;
    }

    .pd-pipw-tool-label {
      font-size: 9px;
      color: var(--pd-muted);
      letter-spacing: 0.3px;
      margin-left: auto; /* push AÇÕES + buttons right; consumables sit on the left */
    }

    /* ---- Canvas area ---- */
    .pd-pipw-stage {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--pd-bg);
      overflow: hidden;
      min-height: 0;
      /* Positioned so the relocated #pd-panels (absolute inset:0) overlays
         the game video while the native PiP is active. */
      position: relative;
    }

    .pd-pipw-stage video {
      display: block;
      width: 100%;
      height: 100%;
      image-rendering: pixelated;
      object-fit: contain;
    }

    /* ---- PiP placeholder (when no video) ---- */
    .pd-pipw-placeholder {
      text-align: center;
      padding: 20px;
    }

    .pd-pipw-placeholder .pd-pipw-placeholder-icon {
      font-size: 32px;
      margin-bottom: 6px;
    }

    .pd-pipw-placeholder p {
      font-size: 10px;
      color: var(--pd-muted);
      margin: 2px 0;
    }

    /* ---- Pop-in button ---- */
    .pd-pipw-popin {
      width: 22px;
      height: 22px;
      background: none;
      border: 1px solid transparent;
      border-radius: 4px;
      color: var(--pd-chrome-accent);
      font-size: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background var(--pd-transition);
      padding: 0;
      line-height: 1;
    }

    .pd-pipw-popin:hover {
      background: rgba(255,255,255,0.1);
    }
  `;
}

/**
 * Build the full HTML content for the PiP window.
 * @param {Document} doc - PiP window document
 * @param {string} origin - Main page origin (for absolute asset URLs)
 */
function buildContent(doc, origin) {
  // Base href so relative asset URLs (e.g. portraits/icons used by the
  // relocated management panels) resolve against the main app origin.
  const base = doc.createElement('base');
  base.href = origin + '/';
  doc.head.appendChild(base);

  // Stylesheet
  const style = doc.createElement('style');
  style.textContent = buildStyles(origin);
  doc.head.appendChild(style);

  // Body — the class lets shared stylesheets target PiP-window-only layout
  // (e.g. panels center against this small window, not the offset stage).
  doc.body.classList.add('pd-pipw-body');
  doc.body.innerHTML = `
    <div class="pd-pipw-titlebar">
      <button class="pd-pipw-menu-toggle" title="Ações">☰</button>
      <div class="pd-pipw-brand">
        <img class="pd-pipw-logo" src="${origin}/assets/brand/logo.png" alt="PD" />
        <span class="pd-pipw-label">PocketDungeon</span>
      </div>
      <button class="pd-pipw-popin" title="Voltar ao site">⊟</button>
    </div>
    <div class="pd-pipw-toolbar" hidden>
      <div class="pd-consumables" id="pd-consumables-pip"></div>
      <span class="pd-pipw-tool-label">AÇÕES</span>
      <button data-action="hero" title="Herói"><img src="${origin}/assets/sprites/ui/icon_hero.png" alt="Herói" class="pd-tool-icon"></button>
      <button data-action="status" title="Status"><img src="${origin}/assets/sprites/ui/icon_status.png" alt="Status" class="pd-tool-icon"></button>
      <button data-action="portal" title="Viajar"><img src="${origin}/assets/sprites/ui/icon_portal.png" alt="Portal" class="pd-tool-icon"></button>
      <button data-action="cube" title="Cubo"><img src="${origin}/assets/sprites/ui/icon_cube.png" alt="Cubo" class="pd-tool-icon"></button>
      <button data-action="account" title="Conta"><img src="${origin}/assets/sprites/ui/icon_account.png" alt="Conta" class="pd-tool-icon"></button>
      <button data-sound title="Som" class="pd-sound-btn"><img src="${origin}/assets/sprites/ui/icon_sound.png" alt="Som" class="pd-tool-icon"></button>
    </div>
    <div class="pd-pipw-stage">
      <video id="pd-pip-video" autoplay muted playsinline></video>
    </div>`;
}

/**
 * Replicate the main document's stylesheets into the PiP window so relocated
 * panels (and any other app DOM) render with their full styling.
 * @param {Document} srcDoc - the main page document
 * @param {Document} destDoc - the PiP window document
 */
function copyStyles(srcDoc, destDoc) {
  // Linked stylesheets — replicate by absolute href (reliable, same-origin).
  srcDoc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    const clone = destDoc.createElement('link');
    clone.rel = 'stylesheet';
    clone.href = link.href; // resolved to an absolute URL
    destDoc.head.appendChild(clone);
  });
  // Inline <style> blocks (e.g. Vite HMR / dev injections).
  srcDoc.querySelectorAll('style').forEach((styleEl) => {
    const clone = destDoc.createElement('style');
    clone.textContent = styleEl.textContent;
    destDoc.head.appendChild(clone);
  });
}

/**
 * Wire control buttons.
 * @param {Window} pipWin - the PiP window (so we can close it from within)
 * @param {(action: string) => void} [onAction] - called when a toolbar button is clicked
 */
function wireButtons(pipWin, onAction) {
  const doc = pipWin.document;

  // Pop-in button — close the PiP window (triggers pagehide → cleanup in opener)
  doc.querySelector('.pd-pipw-popin')?.addEventListener('click', () => {
    pipWin.close();
  });

  // Menu toggle — AÇÕES toolbar starts OPEN (the action bar must show first);
  // ☰ collapses it for players who want the slimmer window.
  const menuToggle = doc.querySelector('.pd-pipw-menu-toggle');
  const toolbar = doc.querySelector('.pd-pipw-toolbar');
  toolbar?.removeAttribute('hidden');
  menuToggle?.classList.add('active');
  menuToggle?.addEventListener('click', () => {
    const open = toolbar.hasAttribute('hidden');
    toolbar.toggleAttribute('hidden', !open);
    menuToggle.classList.toggle('active', open);
  });

  // Action buttons — invoke the callback provided by the opener (direct closure
  // reference avoids window.opener cross-window access which can silently fail).
  doc.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      onAction?.(btn.dataset.action);
    });
  });

  // Sound toggle is self-contained (not a panel): flip the SFX mute and keep
  // the glyph in sync — including with toggles made from the other window.
  doc.querySelectorAll('[data-sound]').forEach((btn) => {
    const sync = (m) => btn.classList.toggle('pd-sound-off', m);
    sync(isMuted());
    btn.addEventListener('click', () => setMuted(!isMuted()));
    bus.on('sfx:muted', sync);
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create the PiP window manager.
 *
 * Usage:
 *   const pipWin = createPiPWindowManager();
 *   await pipWin.open(canvasElement);  // pop out
 *   pipWin.close();                    // pop in
 *
 * @returns {{ open, close, isActive, isSupported }}
 */
export function createPiPWindowManager() {
  let pipWindow = null;
  let stream = null;
  let active = false;
  let _onCloseCb = null;

  const isSupported = () => 'documentPictureInPicture' in window;

  /**
   * Open the real browser PiP window.
   *
   * @param {HTMLCanvasElement} canvasEl - the Phaser canvas to mirror
   * @param {{ onClose?: () => void }} [opts]
   * @returns {Promise<boolean>} true if opened successfully
   */
  async function open(canvasEl, opts = {}) {
    if (!isSupported()) {
      console.warn('PiPWindow: Document Picture-in-Picture API indisponível.');
      return false;
    }

    if (active) {
      console.warn('PiPWindow: PiP já está ativo.');
      return false;
    }

    _onCloseCb = opts.onClose ?? null;

    try {
      // Request the PiP window
      pipWindow = await documentPictureInPicture.requestWindow({
        width: PIP_WIDTH,
        height: PIP_HEIGHT,
      });
    } catch (err) {
      console.error('PiPWindow: falha ao abrir janela PiP.', err);
      return false;
    }

    const origin = window.location.origin;

    // Build content + bring over the app stylesheets so relocated panels render
    // fully styled inside the PiP window.
    buildContent(pipWindow.document, origin);
    copyStyles(document, pipWindow.document);
    wireButtons(pipWindow, opts.onAction);

    // Mirror the Phaser canvas into the PiP video element
    try {
      // 30 fps is enough for a pixel-art idle game — saves CPU
      stream = canvasEl.captureStream(30);
      const video = pipWindow.document.getElementById('pd-pip-video');
      if (video) {
        video.srcObject = stream;
        await video.play().catch(() => {
          // Some browsers require a user gesture; if it fails,
          // the video will show a static frame which is acceptable.
        });
      }
    } catch (err) {
      console.warn('PiPWindow: captureStream falhou, PiP ficará sem vídeo.', err);
    }

    // Let the opener relocate DOM (e.g. the panels container) into the PiP doc.
    opts.onReady?.(pipWindow);

    // Handle user closing the PiP window
    pipWindow.addEventListener('pagehide', () => {
      cleanup();
      _onCloseCb?.();
    });

    active = true;
    return true;
  }

  /** Close the PiP window programmatically. */
  function close() {
    if (pipWindow) {
      pipWindow.close();
    }
    // cleanup() will be called by the pagehide listener above
  }

  function cleanup() {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    active = false;
    pipWindow = null;
  }

  return {
    open,
    close,
    get isActive() {
      return active;
    },
    get isSupported() {
      return isSupported();
    },
  };
}
